import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isString } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { I18nUtils } from '@medable/i18n-core';
import { useBundlePreview } from 'libs.bundle_preview';
import { useLocalizeMessage, Spinner, useToastNotification } from 'libs.nucleus';
import { ActivityRunner } from './activity_runner';
import { ecoa_hooks } from './ecoa_hooks';
import { EIframeMessageTypes } from '../../../../constants/iframe_message.enum';
import { UserLocaleContext } from '../../../../contexts/user_locale/user_locale.context';
import { getApiInstance } from '../../../../helpers/api.helpers';
import { assignAnswerStatus, assignSkippedReason, getStepEndDate, getStepStartDate, } from '../../../../helpers/ecoa.format.helpers';
import { useCurrentEnvironment } from '../../../../hooks/use_current_environment';
import { useSiteContext } from '../../../../hooks/use_site_context';
export const ActivityEcoaView = ({ visitActivity, activityResponse, activityManager, setIsActivityModalClosed, setActivityManagement, participant, }) => {
    const activity = visitActivity.activity.c_group_task.c_assignment;
    const currentVisit = visitActivity.visit;
    const currentActivityResponse = activityResponse;
    const match = useMatch('/site/study/:studyId/participants/:participantId/*');
    const [basePreviewURL, setBasePreviewURL] = useState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { environment } = useCurrentEnvironment();
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const queryClient = useQueryClient();
    const site = useSiteContext();
    const { getUserSelectedLocale } = useContext(UserLocaleContext);
    const siteUserLocale = getUserSelectedLocale();
    const isClinicianInterview = !!activity.c_data_labels?.metadata?.is_clinician_interview;
    const fetchActivityBasedOnParticipantLocale = async () => {
        if (!environment) {
            throw new Error('Environment is undefined');
        }
        const response = await getApiInstance(environment).get(`/v2/c_task/${activity._id}`, {
            headers: {
                'Accept-Language': I18nUtils.cortexToI18nFormat(participant.c_locale),
            },
        });
        return response.data;
    };
    const { data: currentActivityWithParticipantLocaleBundle, isLoading: isCurrentActivityLoading } = useQuery({
        queryKey: ['currentActivityWithParticipantLocaleBundle', activity._id, participant.c_locale],
        queryFn: fetchActivityBasedOnParticipantLocale,
        enabled: isClinicianInterview,
    });
    const fetchAWSBundle = async () => {
        if (!activity.c_html_bundle) {
            throw new Error('HTML bundle is undefined');
        }
        let bundleURL = activity.c_html_bundle.url;
        if (isClinicianInterview &&
            currentActivityWithParticipantLocaleBundle &&
            currentActivityWithParticipantLocaleBundle.c_html_bundle) {
            const localeBasedBundleURL = currentActivityWithParticipantLocaleBundle.c_html_bundle.url;
            if (localeBasedBundleURL) {
                bundleURL = localeBasedBundleURL;
            }
        }
        const response = await fetch(bundleURL);
        const blobResponse = await response.blob();
        return blobResponse;
    };
    const { data: activityBlob, isLoading } = useQuery({
        queryKey: [
            'activityBundle',
            activity._id,
            isClinicianInterview ? currentActivityWithParticipantLocaleBundle : null,
        ],
        queryFn: fetchAWSBundle,
        enabled: !isClinicianInterview ||
            (isClinicianInterview && !isCurrentActivityLoading && !!currentActivityWithParticipantLocaleBundle),
    });
    const { bundlePreview: activityBundleView } = useBundlePreview(activityBlob);
    const clearActivityResponses = () => {
        currentActivityResponse.clearStepResponses();
        activityManager.clearActivityResponse();
        queryClient.invalidateQueries({
            queryKey: [
                'activityBundle',
                activity._id,
                isClinicianInterview ? currentActivityWithParticipantLocaleBundle : null,
            ],
        });
        queryClient.invalidateQueries({
            queryKey: ['currentActivityWithParticipantLocaleBundle', activity._id, participant.c_locale],
        });
        setActivityManagement(null);
    };
    const handleIframeMessage = async ({ data }) => {
        const { type, message } = data;
        if (type === EIframeMessageTypes.CANCEL) {
            clearActivityResponses();
            setIsActivityModalClosed();
            return;
        }
        if (type === EIframeMessageTypes.SUBMIT) {
            const { responses, events } = message;
            const { c_steps: { data: steps }, } = activity;
            responses.forEach(({ stepId, value, original_value, status, unit, reason }) => {
                const c_step = steps.find(({ c_ecoa_step_id }) => c_ecoa_step_id === stepId);
                if (!c_step) {
                    return;
                }
                const c_start_date = getStepStartDate(stepId, events);
                const c_end_date = getStepEndDate(stepId, events);
                let c_value = value;
                if (c_step.c_type === 'text_choice' && isString(value)) {
                    c_value = [value];
                }
                const response = {
                    c_step,
                    c_value,
                    c_start_date,
                    c_end_date,
                    c_original_value: original_value,
                    c_unit: unit,
                    ...assignAnswerStatus(status),
                    ...assignSkippedReason(status, reason),
                };
                currentActivityResponse.updateStepResponse(response);
            });
            try {
                setIsSubmitting(true);
                await currentActivityResponse.upload({ environment });
                addNotification({
                    title: '',
                    subtitle: translate('info.activity.uploaded'),
                    type: 'success',
                });
                if (currentVisit.customVisitType === 'onboarding') {
                    queryClient.invalidateQueries({
                        queryKey: ['onboardingActivities', currentVisit.c_group, siteUserLocale],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['onboardingDetails', 'onboardingResponses', match?.params.participantId, siteUserLocale],
                    });
                }
                else {
                    queryClient.invalidateQueries({
                        queryKey: ['siteActivities', match?.params.participantId, currentVisit.c_visit, site?._id, siteUserLocale],
                    });
                    queryClient.invalidateQueries({
                        queryKey: ['participantActivities', match?.params.participantId],
                    });
                }
                currentActivityResponse.clearStepResponses();
                setActivityManagement(null);
                setIsSubmitting(false);
                setIsActivityModalClosed();
            }
            catch (error) {
                setIsSubmitting(false);
                addNotification({
                    title: '',
                    subtitle: error?.response?.data?.message ?? translate('error.activity.upload'),
                    type: 'error',
                });
            }
        }
    };
    useEffect(() => {
        if (activityBundleView) {
            activityBundleView.setScript(ecoa_hooks);
            const url = activityBundleView.getScreenURL(''); // gets the first screen on the activity
            setBasePreviewURL(url);
        }
        window.addEventListener('message', handleIframeMessage);
        return () => {
            window.removeEventListener('message', handleIframeMessage);
        };
    }, [activityBundleView]);
    if (isLoading || !basePreviewURL) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    return (_jsxs(_Fragment, { children: [isSubmitting && _jsx(Spinner, { wrapper: 'full' }), _jsx(ActivityRunner, { basePreviewURL: basePreviewURL, participantId: match?.params.participantId || '', visitId: currentVisit.c_visit || '', activityId: activity._id })] }));
};
